import './App.css';
import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import SyncFollowers from './components/SyncFollowers';
import ButtonGroup from './components/ButtonGroup';
import SyncInfoModal from './components/modals/SyncInfoModal';
import ElectAnimationModal from './components/modals/ElectAnimationModal';
import CountDownAnimationModal from './components/modals/CountDownAnimationModal';
import FullPageVideo from './components/FullPageVideo';
import CountDownVideo from './components/CountDownVideo';
import UserModal from './components/modals/UserModal';
import Cookies from 'js-cookie';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CLIENT_ID = process.env.REACT_APP_TWITCH_CLIENT_ID;
const REDIRECT_URI = encodeURIComponent(process.env.REACT_APP_REDIRECT_URI);
console.log("Twitch Client ID:", CLIENT_ID);

const App = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAnimationOpen, setIsAnimationOpen] = useState(false);
  const [isCountdownOpen, setIsCountdownOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [marketerData, setMarketerData] = useState(null);

  const [receivedUsersData, setReceivedUsersData] = useState([]);
  const [electedUserData, setElectedUserData] = useState(null);
  const [isFullPageVideoOpen, setIsFullPageVideoOpen] = useState(false);
  const [isCountDownVideoOpen, setIsCountDownVideoOpen] = useState(false);

  const [isSoundOn, setIsSoundOn] = useState(false);
  const [volume, setVolume] = useState(100);

  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    if (params.has('access_token')) {
      const accessToken = params.get('access_token');
      Cookies.set('twitch-access-token', accessToken);
      fetchUserInfo(accessToken);
      window.location.hash = '';
    } else {
      const accessToken = Cookies.get('twitch-access-token');
      if (accessToken) {
        fetchUserInfo(accessToken);
      }
    }
  }, []);

  const handleReceiveUsersFromSyncFollowers = (usersData) => {
    setReceivedUsersData(usersData);
  };

  const ElectedUser = (electedUser) => {
    setElectedUserData(electedUser);
  };

  const playFullPageVideo = () => setIsFullPageVideoOpen(true);

  const closeFullPageVideo = () => {
    setTimeout(() => setIsFullPageVideoOpen(false), 300);
    setIsAnimationOpen(true);
  };

  const playCountDownVideo = () => setIsCountDownVideoOpen(true);

  const closeCountDownVideo = () => {
    setTimeout(() => setIsCountDownVideoOpen(false), 300);
    setIsCountdownOpen(true);
  };

  const fetchUserInfo = async (accessToken) => {
    try {
      const adminToken = localStorage.getItem('admin_token'); // Assuming admin_token is stored in localStorage
      console.log("Admin Token:", adminToken);
      const userResponse = await fetch('https://api.twitch.tv/helix/users', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Client-ID': CLIENT_ID,
        },
      });
      const userJson = await userResponse.json();

      if (userResponse.ok) {
        const twitchUser = userJson.data[0];
        setIsAuthenticated(true);
        setUser({
          accessToken: accessToken,
          id: twitchUser.id,
          name: twitchUser.login,
        });

        // Use email from the initial user response if available, otherwise null
        const userEmail = twitchUser.email || null;

        // Call backend to upsert marketer
        const backendUrl = process.env.REACT_APP_API_URL;
        const adminToken = localStorage.getItem('admin_token'); // Assuming admin_token is stored in localStorage

        // 1. Fetch all marketers
        const allMarketersRes = await axios.get(`${backendUrl}/api/all/marketors`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        const allMarketers = allMarketersRes.data;

        // 2. Check if marketer exists
        let existingMarketer = allMarketers.find(m => m.twitch_user_id === twitchUser.id);
        let updatedMarketerData;

        if (existingMarketer) {
          console.log(`Twitch user ID ${twitchUser.id} matches existing marketer. Updating profile.`);
          // Marketer exists, update their info
          const updatePayload = {
            twitch_display_name: twitchUser.display_name || twitchUser.login,
            email: userEmail,
            name: twitchUser.display_name || twitchUser.login,
            refid: existingMarketer.refid || (twitchUser.display_name || twitchUser.login), // Keep existing refid or set new
          };
          const updateRes = await axios.patch(`${backendUrl}/api/all/marketors/${existingMarketer.id}`, updatePayload, {
            headers: { Authorization: `Bearer ${adminToken}` },
          });
          updatedMarketerData = updateRes.data;
        } else {
          console.log(`Twitch user ID ${twitchUser.id} not found. Signing up as new marketer.`);
          // Marketer does not exist, create a new one
          const createPayload = {
            twitch_user_id: twitchUser.id,
            twitch_display_name: twitchUser.display_name || twitchUser.login,
            email: userEmail,
            name: twitchUser.display_name || twitchUser.login,
            refid: twitchUser.display_name || twitchUser.login, // Initial refid
          };
          const createRes = await axios.post(`${backendUrl}/api/all/marketors`, createPayload, {
            headers: { Authorization: `Bearer ${adminToken}` },
          });
          if (createRes.status === 201) {
            console.log(`New marketer created successfully for Twitch user ID: ${twitchUser.id}`);
          } else {
            console.error(`Failed to create new marketer for Twitch user ID: ${twitchUser.id}. Status: ${createRes.status}`);
          }
          updatedMarketerData = createRes.data;
        }
        setMarketerData(updatedMarketerData);
        console.log("Marketer Data from Backend:", updatedMarketerData);

      } else {
        toast.error('Failed to fetch user data.');
      }
    } catch (error) {
      console.error('Error fetching user data or upserting marketer:', error);
      toast.error('An error occurred during login.');
    }
  };

  // --- FIX: Add all required scopes ---
  const handleLogin = () => {
    const scopes = [
      'moderator:read:followers',
      'moderator:read:chatters',
      'chat:read',
      'user:read:follows',
      'user:read:email'
    ].join(' ');
    window.location.href = `https://id.twitch.tv/oauth2/authorize?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=token&scope=${encodeURIComponent(scopes)}`;
  };

  const handleLogout = () => {
    Cookies.remove('twitch-access-token');
    setIsAuthenticated(false);
    setUser(null);
    setMarketerData(null);
    // Send logout event to Electron main process to clear session data
    if (window.require) {
      const { ipcRenderer } = window.require('electron');
      ipcRenderer.send('logout');
    } else {
      window.location.reload(true);
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const openUserModal = () => setIsUserModalOpen(true);
  const closeUserModal = () => setIsUserModalOpen(false);

  const openAnimation = () => {
    if (isAuthenticated) {
      if (receivedUsersData.length !== 0) {
        playFullPageVideo();
      } else {
        toast.info('There are no any followers.', {className: 'custom-toast'});
      }
    } else {
      toast.warning('You should login and then syncronize followers.', {className: 'custom-toast'});
    }
  };

  const closeAnimation = () => setIsAnimationOpen(false);

  const openCountDownAnimation = () => {
    if (isAuthenticated) {
      electedUserData == null
        ? toast.warning('There is no any selected winner. You should select a winner in "Start selection".', {className: 'custom-toast'})
        : playCountDownVideo();
    } else {
      toast.warning('You should login and then syncronize followers.', {className: 'custom-toast'});
    }
  };

  const closeCountDownAnimation = () => setIsCountdownOpen(false);

  const toggleSound = () => setIsSoundOn(!isSoundOn);

  return (
    <div className="total-area">
      <Header
        isAuthenticated={isAuthenticated}
        user={user}
        onLogin={isAuthenticated ? openUserModal : handleLogin}
        openModal={openModal}
        toggleSound={toggleSound}
        isSoundOn={isSoundOn}
        volume={volume}
        setVolume={setVolume}
      />
      <div className="main-body">
        <SyncFollowers
          isAuthenticated={isAuthenticated}
          user={user}
          sendUsersToApp={handleReceiveUsersFromSyncFollowers}
          electedUser={electedUserData}
          isSoundOn={isSoundOn}
          volume={volume}
          setVolume={setVolume}
        />
  <ButtonGroup
    openAnimation={openAnimation}
    openCountDownAnimation={openCountDownAnimation}
    isSoundOn={isSoundOn}
    volume={volume}
    setVolume={setVolume}
    streamerUsername={user?.name}
    winnerUsername={electedUserData?.user_name}
/>

      </div>
      <Footer />
      <SyncInfoModal isOpen={isModalOpen} onClose={closeModal} isSoundOn={isSoundOn} volume={volume}/>
      <UserModal isOpen={isUserModalOpen} onClose={closeUserModal} user={user} marketerData={marketerData} setMarketerData={setMarketerData} onLogout={handleLogout} />
      {isFullPageVideoOpen && (
        <FullPageVideo
          videoSource="/images/selection_load.mp4"
          onVideoEnd={closeFullPageVideo}
          isSoundOn={isSoundOn}
          volume={volume}
        />
      )}
      {isCountDownVideoOpen && (
        <CountDownVideo
          videoSource="/images/countdown_load.mp4"
          onVideoEnd={closeCountDownVideo}
          isSoundOn={isSoundOn}
          volume={volume}
        />
      )}
      {isAnimationOpen && (
        <ElectAnimationModal
          isOpen={isAnimationOpen}
          onClose={closeAnimation}
          usersData={receivedUsersData}
          onAnimationComplete={ElectedUser}
          isSoundOn={isSoundOn}
          volume={volume}
          setVolume={setVolume}
        />
      )}
      {isCountdownOpen && (
        <CountDownAnimationModal
          isOpen={isCountdownOpen}
          onClose={closeCountDownAnimation}
          electedUser={electedUserData}
          user={user}
          isSoundOn={isSoundOn}
          volume={volume}
          marketerData={marketerData}
        />
      )}
      <ToastContainer
        position="top-right"
        autoClose={300000000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
};

export default App;
